import { Formik } from "formik";
import { get } from "lodash";
import { useTranslation } from "react-i18next";
import { BiCheck, BiChevronLeft, BiTrash } from "react-icons/bi";
import { useNavigate } from "react-router";
import AdvisorSellerCodeMappingsCard from "shared/components/AdvisorSellerCodeMappingsCard";
import Card from "shared/components/Card";
import Header from "shared/components/Header";
import Loading from "shared/components/Spin";
import { useQuery } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import { USERS } from "utils/api/graphql/queries/users";
import { SCREENS, USER_TYPES } from "utils/constants";
import Advisor from "./UserTypes/Advisor";
import Default from "./UserTypes/Default";
import { collapseSkillsForForm } from "../shared/userSkills";
import validation from "./validation";

const SELLER_CODE_USER_TYPES = new Set([
  USER_TYPES.INTERNAL_ADVISOR.type,
  USER_TYPES.EXTERNAL_ADVISOR.type,
  USER_TYPES.BROKER_EMPLOYEE.type,
]);

const Detail = ({ onSubmit, data, cancel }) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.USER);
  const navigate = useNavigate();
  const { data: users, loading: usersLoading } = useQuery(USERS, {
    variables: {
      isActive: true,
      where: { meta: { type: USER_TYPES.INTERNAL_SUPERVISOR.type } },
      isIn: { poles: { id: get(data, "poles", []).map(({ id }) => id) } },
    },
  });
  const DETAIL = t("DETAIL", { returnObjects: true });
  const generateUserForm = (formProps, setFieldValue) => ({
    [true]: (
      <Advisor
        key="advisor"
        formProps={formProps}
        setFieldValue={setFieldValue}
      />
    ),
    [false]: (
      <Default
        key="default"
        formProps={formProps}
        setFieldValue={setFieldValue}
      />
    ),
  });
  if (usersLoading) return <Loading />;
  return (
    <Formik
      initialValues={{
        ...data,
        skills: collapseSkillsForForm(get(data, "skills", [])),
        supervisors: get(users, "users.data", []).map((s) => s?.id),
      }}
      onSubmit={onSubmit}
      validationSchema={validation(
        t(`${NAME_SPACES.ERRORS}:VALIDATIONS`, { returnObjects: true }),
      )}
    >
      {({ handleSubmit, ...formProps }) => {
        const metaType = get(formProps, "values.meta.type");
        const orgId = get(formProps, "values.organization.id");
        const userId = get(formProps, "values.id");
        const showSellerCodes =
          SELLER_CODE_USER_TYPES.has(metaType) && orgId && userId;

        return [
          <Header
            key={"header"}
            title={DETAIL.HEADER.TITLE}
            subtitle={DETAIL.HEADER.ACTION}
            actions={[
              {
                text: DETAIL.HEADER.DISCARD,
                onClick: cancel,
                type: "primary--outlined",
                icon: <BiChevronLeft size={20} />,
              },
              {
                text: DETAIL.HEADER.DELETE,
                onClick: () =>
                  navigate(
                    `${SCREENS.PRIVATE.ADMIN.USERS.DELETE.path}/${data.id}`,
                    { replace: true },
                  ),
                type: "danger--outlined",
                icon: <BiTrash size={20} />,
              },
              {
                text: DETAIL.HEADER.SAVE,
                onClick: handleSubmit,
                type: "primary",
                icon: <BiCheck size={20} />,
                disabled: !formProps.dirty,
              },
            ]}
          />,

          generateUserForm(formProps)[
            metaType === USER_TYPES.INTERNAL_ADVISOR.type
          ],

          ...(showSellerCodes
            ? [
                <Card key="seller-code-mappings" type="simple" head={false}>
                  <div className="m-top-16">
                    <AdvisorSellerCodeMappingsCard
                      organizationId={orgId}
                      lockedUserId={userId}
                      nameSpace={NAME_SPACES.PRIVATE.ADMIN.USER}
                      showRangesHint={false}
                    />
                  </div>
                </Card>,
              ]
            : []),
        ];
      }}
    </Formik>
  );
};

export default Detail;
